import { Injectable, Inject } from '@angular/core';
import { TRANSLATIONS } from './lang/translations';
import { catchError, map } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { ErrorObservable } from 'rxjs/observable/ErrorObservable';
import * as moment from 'moment-timezone';
import * as humanizeDuration from 'humanize-duration';
import { APP_CONFIG } from '../config/config.service';
import { Subject } from 'rxjs/Subject';

/**
 * All languages (list from TWA)
 */
export const LANG = {
    en: 'en',
    de: 'de',
    fr: 'fr',
    nl: 'nl',
    it: 'it',
    es: 'es',
    pl: 'pl',
    sv_SE: 'sv_SE',
    pt: 'pt',
    ru: 'ru',
    zh_CN: 'zh_CN',
    hu: 'hu',
    en_US: 'en_US',
    ko: 'ko',
    ja: 'ja'
};

/**
 * Languages exceptions for 'humanize-duration' lib
 */
const LANG_HUMANIZE = {
    en: 'en',
    sv: 'sv'
};

/**
 * Languages exceptions for 'moment-timezone' lib
 */
const LANG_MOMENT = {
    zh_CN: 'zh-cn', // Chinese, simplified
    en: 'en', // English
    sv: 'sv' // Swedish
};

/**
 * Languages exceptions for inline manual (question icon on UI)
 */
const LANG_INLINE_MANUAL = {
    en: 'en',
    qu: 'qu',
    sv: 'sv',
    zh: 'zh'
};

/**
 * Translation service presents all logic connected to translations and languages
 */
@Injectable()
export class TranslationService {

    /**
     * local variable _currentLang
     * @type {string}
     * @private
     */
    private _currentLang: string = LANG.en;

    /**
     * local Subject _curLangSubject for working with subscriptions
     * @type {Subject<any>}
     * @private
     */
    private _curLangSubject: Subject<string> = new Subject();

    /**
     * The "constructor"
     * @param {HttpClient} http
     * @param _translations
     */
    constructor(private http: HttpClient,
                @Inject(TRANSLATIONS) private _translations: any) {
    }

    /**
     * Error handler
     * @param {HttpErrorResponse} error
     * @returns {ErrorObservable}
     */
    public handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('An error occurred:', error.error.message);
        } else if (error.error) {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong,
            console.error(
                `Backend returned code ${error.status}, ` +
                `body was: `);
            console.error(error.error);
        }
        return new ErrorObservable(
            'Error occurred: ' + ((error && error.error && error.error.message) ?
            error.error.message : error.status + ' ' + error.statusText));
    }

    /**
     * @returns {string} current lang
     */
    public get currentLang() {
        this._curLangSubject.next(this._currentLang);
        return this._currentLang;
    }

    /**
     * Used for other components to subscribe on lang value change
     * @param fn
     * @returns {Subscription} subscription from _curLangSubject
     */
    public subscribeCurLang(fn) {
        return this._curLangSubject.distinctUntilChanged().subscribe(fn);
    }

    /**
     * @returns {string} current lang for 'humanize-duration' lib
     */
    public get currentLocaleHumanize(): string {
        switch (this._currentLang) {
            case LANG.en_US:
                return LANG_HUMANIZE.en;
            case LANG.sv_SE:
                return LANG_HUMANIZE.sv;
            default:
                return this._currentLang;
        }
    }

    /**
     * Get date with timezone (without conversion to this timezone)
     * @param {Date} date
     * @param {string} timezone
     * @param {boolean} hideTimezone
     * @returns {string}
     */
    public getUtcDate(date: Date, timezone: string, hideTimezone?: boolean) {
        if (date) {
            let df = moment.utc(date).format(this.getDateFormat());
            return (hideTimezone || !timezone) ? df : df + ' (' + timezone + ')';
        } else {
            return '';
        }
    }

    /**
     * Get formatted date with timezone (and converted to this timezone)
     * @param {Date} date
     * @param {string} timezone
     * @param {boolean} hideTimezone - true if timezone should be hidden
     * @returns {string} formatted date
     */
    public getFormattedDate(date: Date, timezone: string, hideTimezone?: boolean) {
        if (!timezone) {
            return this.getUtcDate(date, timezone, hideTimezone);
        } else if (date && timezone) {
            let df = moment.tz(date, timezone).format(this.getDateFormat());
            return hideTimezone ? df : df + ' (' + timezone + ')';
        } else {
            return '';
        }
    }

    /**
     * Get converted time to given timezone
     * @param {Date} date
     * @param {string} timezone
     * @returns {string} formatted time
     */
    public getFormattedTime(date: Date) {
        if (date) {
            let format = this.currentLang === LANG.en_US ? 'hh:mm A' : 'HH:mm';
            return moment.utc(date).format(format);
        } else {
            return '';
        }
    }

    /**
     * Get humanized duration between two dates in current language, used 'humanize-duration' lib
     * @param {string} duration
     * @returns {string} humanized duration
     */
    public dateDiffToHumanRead(duration: string): string {
        let dur = moment.duration(duration);
        let totalDuration = humanizeDuration(dur, {
            units: ['d', 'h', 'm'],
            language: this.currentLocaleHumanize, round: true
        });
        return dur < 0 ? '-' + totalDuration : totalDuration;
    }

    /**
     * Set current language and put locale to 'moment' lib and inline manual
     * @param {string} lang
     */
    public use(lang: string): void {
        switch (lang) {
            case LANG.en:
            case LANG.de:
            case LANG.fr:
            case LANG.nl:
            case LANG.it:
            case LANG.es:
            case LANG.pl:
            case LANG.pt:
            case LANG.ru:
            case LANG.hu:
            case LANG.ko:
            case LANG.ja:
                this._currentLang = lang;
                moment.locale(lang);
                break;
            case LANG.en_US:
                this._currentLang = lang;
                moment.locale(LANG_MOMENT.en);
                break;
            case LANG.sv_SE:
                this._currentLang = lang;
                moment.locale(LANG_MOMENT.sv);
                break;
            case LANG.zh_CN:
                this._currentLang = lang;
                moment.locale(LANG_MOMENT.zh_CN);
                break;
            default:
                this._currentLang = LANG.en;
                moment.locale(LANG_MOMENT.en);
        }
    }

    /**
     * Get translated value by key (property) for current language
     * @param {string} key
     * @returns {string} translated value
     */
    public get(key: string) {
        return this.translate(key);
    }

    /**
     * Get translated value by key with params in curly brackets for current language
     * @param {string} key
     * @param params
     * @returns {string} translated value with params
     */
    public getWithParams(key: string, params: any) {
        let translation = this.translate(key);
        for (const param of params) {
            translation = translation ? translation.replace('{}', param) : translation;
        }
        return translation;
    }

    /**
     * Private method: get translated value by key (property) for current language
     * @param {string} key
     * @returns {string} translated value
     */
    private translate(key: string): string {
        const translation = key;
        if (this._translations[this.currentLang] && this._translations[this.currentLang][key]) {
            return this._translations[this.currentLang][key];
        }
        return translation;
    }

    /**
     * Get date format depending on user locale
     * @returns {string | string}
     */
    private getDateFormat() {
        return this.currentLang === LANG.en_US ? 'MMM D, YYYY, hh:mm A' : 'D MMM YYYY HH:mm';
    }
}



// WEBPACK FOOTER //
// ./src/app/services/translation/translation.service.ts